import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import SortIcon from '@mui/icons-material/Sort';
import {
  Badge,
  Box,
  Button,
  Container,
  FormControl,
  InputAdornment,
  Menu,
  MenuItem,
  Select,
  SelectChangeEvent,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import React, { useMemo, useState } from 'react';
import SkeletonLoader from '../../../components/common/SkeletonLoader';
import { BookingStatus } from '../../../happypaws-shared/src/Booking/models/Booking';
import { ServiceType, SERVICE_TYPE_LABELS } from '../../../happypaws-shared/src/Services/services';
import { useGetSitterBookings } from '../../../hooks/bookings/useGetSitterBookings';
import { useUpdateBookingStatus } from '../../../hooks/bookings/useUpdateBookingStatus';
import BookingCard from './components/BookingCard';

import SitterHeader from '../SitterHeader/SitterHeader';

const SitterBookings: React.FC = () => {
  const { data: bookings, isLoading } = useGetSitterBookings();
  const { mutate: updateStatus } = useUpdateBookingStatus();
  const [activeTab, setActiveTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'date' | 'price'>('date');
  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null);
  const [serviceFilter, setServiceFilter] = useState<ServiceType | null>(null);

  const tabLabels = ['All', 'Pending', 'Confirmed', 'In Progress', 'Completed', 'Cancelled'];

  const getStatusFromTab = (tabIndex: number): BookingStatus | null => {
    switch (tabIndex) {
      case 1:
        return BookingStatus.PENDING;
      case 2:
        return BookingStatus.CONFIRMED;
      case 3:
        return BookingStatus.COMPLETED;
      case 4:
        return BookingStatus.CANCELLED;
      default:
        return null;
    }
  };

  const getCountByStatus = (status: BookingStatus | null) => {
    if (!bookings) return 0;
    if (status === null) return bookings.length;
    return bookings.filter((b) => b.status === status).length;
  };

  const filteredAndSortedBookings = useMemo(() => {
    if (!bookings) return [];

    let filtered = bookings;

    if (serviceFilter) {
      filtered = filtered.filter((b) => b.serviceType === serviceFilter);
    }

    const statusFilter = getStatusFromTab(activeTab);
    if (statusFilter) {
      filtered = filtered.filter((b) => b.status === statusFilter);
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (b) =>
          `${b.clientFirstName} ${b.clientLastName}`.toLowerCase().includes(query) ||
          b.pets.some((p) => p.petName.toLowerCase().includes(query)) ||
          b.serviceType.toLowerCase().includes(query)
      );
    }

    filtered = [...filtered].sort((a, b) => {
      if (sortBy === 'price') return Number(b.totalPrice) - Number(a.totalPrice);
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });

    return filtered;
  }, [bookings, activeTab, searchQuery, sortBy, serviceFilter]);

  const handleAccept = (id: string) => {
    updateStatus({ bookingId: id, status: BookingStatus.CONFIRMED });
  };

  const handleDecline = (id: string) => {
    updateStatus({ bookingId: id, status: BookingStatus.CANCELLED });
  };

  const handleCancel = (id: string) => {
    updateStatus({ bookingId: id, status: BookingStatus.CANCELLED });
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const handleSortChange = (event: SelectChangeEvent) => {
    setSortBy(event.target.value as 'date' | 'price');
  };

  const handleFilterClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const handleView = (id: string) => {
    console.log('Viewing booking:', id);
  };

  const handleMessage = (id: string) => {
    console.log('Messaging client for booking:', id);
  };

  const handleReview = (id: string) => {
    console.log('Leaving review for booking:', id);
  };

  return (
    <Box sx={{ backgroundColor: '#FAF8F5', minHeight: '100vh' }}>
      <SitterHeader />
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Page Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
            Bookings
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {filteredAndSortedBookings.length} bookings found
          </Typography>
        </Box>

        {/* Status Tabs */}
        <Box sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 500,
                fontSize: { xs: '0.8rem', sm: '1rem' },
              },
            }}
          >
            {tabLabels.map((label, index) => (
              <Tab
                key={label}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {label}
                    <Badge
                      badgeContent={getCountByStatus(getStatusFromTab(index))}
                      sx={{
                        '& .MuiBadge-badge': {
                          backgroundColor: activeTab === index ? 'primary.main' : '#E0E0E0',
                          color: activeTab === index ? 'white' : 'text.secondary',
                          fontWeight: 600,
                        },
                      }}
                    />
                  </Box>
                }
              />
            ))}
          </Tabs>
        </Box>

        {/* Search and Filter Bar */}
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            mb: 3,
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          <TextField
            placeholder="Search by client or pet name..."
            value={searchQuery}
            onChange={handleSearchChange}
            sx={{ flex: '1 1 300px' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />

          <Button
            variant={serviceFilter ? 'contained' : 'outlined'}
            startIcon={<FilterListIcon />}
            onClick={handleFilterClick}
            sx={{
              minWidth: '120px',
              ...(serviceFilter && {
                backgroundColor: '#178B8E',
                '&:hover': { backgroundColor: '#0F6164' },
              }),
            }}
          >
            {serviceFilter ? SERVICE_TYPE_LABELS[serviceFilter] : 'Filter'}
          </Button>

          <FormControl sx={{ minWidth: 150 }}>
            <Select
              value={sortBy}
              onChange={handleSortChange}
              size="medium"
              startAdornment={
                <InputAdornment position="start">
                  <SortIcon />
                </InputAdornment>
              }
            >
              <MenuItem value="date">Sort by Date</MenuItem>
              <MenuItem value="price">Sort by Price</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Filter Menu */}
        <Menu anchorEl={filterAnchorEl} open={Boolean(filterAnchorEl)} onClose={handleFilterClose}>
          <MenuItem
            onClick={() => {
              setServiceFilter(null);
              handleFilterClose();
            }}
          >
            All Services
          </MenuItem>
          {Object.values(ServiceType).map((service) => (
            <MenuItem
              key={service}
              onClick={() => {
                setServiceFilter(service);
                handleFilterClose();
              }}
            >
              {SERVICE_TYPE_LABELS[service]}
            </MenuItem>
          ))}
        </Menu>

        <SkeletonLoader isLoading={isLoading} count={3}>
          {filteredAndSortedBookings.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8, backgroundColor: 'white', borderRadius: 2 }}>
              <Typography variant="h6" color="text.secondary">
                No bookings found
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Try adjusting your filters or search query
              </Typography>
            </Box>
          ) : (
            <Box>
              {filteredAndSortedBookings.map((booking) => (
                <BookingCard
                  key={booking.id}
                  booking={booking}
                  onAccept={handleAccept}
                  onDecline={handleDecline}
                  onView={handleView}
                  onMessage={handleMessage}
                  onCancel={handleCancel}
                  onReview={handleReview}
                />
              ))}
            </Box>
          )}
        </SkeletonLoader>
      </Container>
    </Box>
  );
};

export default SitterBookings;
